import React, { useState, useEffect, useCallback, useRef } from "react";
import { useApi } from "../../hooks/useApi";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import frLocale from "@fullcalendar/core/locales/fr";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
    CalendarDays,
    Plus,
    Trash2,
    MapPin,
    Clock,
    FolderKanban,
    User,
    Filter,
    Loader2,
    Download,
} from "lucide-react";
import { API_URL } from "../../lib/utils";
import { useAuth } from "../../contexts/AuthContext";

const EVENT_TYPE_LABELS = {
    meeting: "Réunion",
    task_deadline: "Tâche",
    project_deadline: "Projet",
    reminder: "Rappel",
    custom: "Événement",
};

const EVENT_TYPE_COLORS = {
    project_deadline: "#DC2626",
    task_deadline: "#F97316",
    meeting: "#2563EB",
    reminder: "#8B5CF6",
    custom: "#059669",
};

const defaultForm = {
    title: "",
    description: "",
    start: "",
    end: "",
    all_day: false,
    event_type: "meeting",
    color: "",
    project_id: "",
    assigned_to: "",
    location: "",
};

export const CalendarPage = () => {
    const { get, post, put, del } = useApi();
    const { token } = useAuth();
    const calendarRef = useRef(null);
    const [events, setEvents] = useState([]);
    const [projects, setProjects] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters
    const [filterType, setFilterType] = useState("all");
    const [filterProject, setFilterProject] = useState("all");
    const [filterUser, setFilterUser] = useState("all");
    const [showFilters, setShowFilters] = useState(false);

    // Dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [formData, setFormData] = useState({ ...defaultForm });
    const [isEditing, setIsEditing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [exporting, setExporting] = useState(false);

    const handleExportIcal = async () => {
        setExporting(true);
        try {
            const res = await fetch(`${API_URL}/calendar/export/ical`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Export failed");
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "orkestria_calendar.ics";
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success("Calendrier exporte avec succes");
        } catch {
            toast.error("Erreur lors de l'export");
        } finally {
            setExporting(false);
        }
    };

    const fetchEvents = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (filterType !== "all") params.set("event_type", filterType);
            if (filterProject !== "all") params.set("project_id", filterProject);
            if (filterUser !== "all") params.set("user_id", filterUser);
            const queryStr = params.toString() ? `?${params.toString()}` : "";
            const data = await get(`/calendar/events${queryStr}`);
            setEvents(data);
        } catch (error) {
            toast.error("Erreur lors du chargement des événements");
        }
    }, [filterType, filterProject, filterUser, get]);

    const fetchMeta = useCallback(async () => {
        const results = await Promise.allSettled([
            get("/projects"),
            get("/employees"),
        ]);
        if (results[0].status === "fulfilled") setProjects(results[0].value);
        if (results[1].status === "fulfilled") setEmployees(results[1].value);
    }, [get]);

    useEffect(() => {
        const init = async () => {
            await fetchMeta();
            await fetchEvents();
            setLoading(false);
        };
        init();
    }, [fetchEvents, fetchMeta]);

    const calendarEvents = events.map((e) => ({
        id: e.event_id,
        title: e.title,
        start: e.start,
        end: e.end || e.start,
        allDay: e.all_day,
        backgroundColor: e.color || EVENT_TYPE_COLORS[e.event_type] || EVENT_TYPE_COLORS.custom,
        borderColor: e.color || EVENT_TYPE_COLORS[e.event_type] || EVENT_TYPE_COLORS.custom,
        extendedProps: {
            ...e,
        },
    }));

    const handleDateClick = (info) => {
        setIsEditing(false);
        setFormData({
            ...defaultForm,
            start: info.dateStr.includes("T") ? info.dateStr.slice(0, 16) : info.dateStr + "T09:00",
            end: info.dateStr.includes("T")
                ? new Date(new Date(info.dateStr).getTime() + 3600000).toISOString().slice(0, 16)
                : info.dateStr + "T10:00",
            all_day: !info.dateStr.includes("T"),
        });
        setDialogOpen(true);
    };

    const handleEventClick = (info) => {
        const props = info.event.extendedProps;
        setSelectedEvent(props);
        setDetailDialogOpen(true);
    };

    const openEditDialog = () => {
        if (!selectedEvent) return;
        setIsEditing(true);
        setFormData({
            title: selectedEvent.title || "",
            description: selectedEvent.description || "",
            start: selectedEvent.start ? selectedEvent.start.slice(0, 16) : "",
            end: selectedEvent.end ? selectedEvent.end.slice(0, 16) : "",
            all_day: selectedEvent.all_day || false,
            event_type: selectedEvent.event_type || "meeting",
            color: selectedEvent.color || "",
            project_id: selectedEvent.project_id || "",
            assigned_to: selectedEvent.assigned_to || "",
            location: selectedEvent.location || "",
        });
        setDetailDialogOpen(false);
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title.trim()) {
            toast.error("Le titre est requis");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                ...formData,
                color: formData.color || EVENT_TYPE_COLORS[formData.event_type] || EVENT_TYPE_COLORS.custom,
                project_id: formData.project_id || null,
                assigned_to: formData.assigned_to || null,
            };

            if (isEditing && selectedEvent) {
                await put(`/calendar/events/${selectedEvent.event_id}`, payload);
                toast.success("Événement mis à jour");
            } else {
                await post("/calendar/events", payload);
                toast.success("Événement créé");
            }
            setDialogOpen(false);
            setFormData({ ...defaultForm });
            setSelectedEvent(null);
            await fetchEvents();
        } catch (error) {
            toast.error("Erreur lors de la sauvegarde");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedEvent || selectedEvent.source !== "manual") return;
        try {
            await del(`/calendar/events/${selectedEvent.event_id}`);
            toast.success("Événement supprimé");
            setDetailDialogOpen(false);
            setSelectedEvent(null);
            await fetchEvents();
        } catch (error) {
            toast.error("Erreur lors de la suppression");
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    const activeFilterCount = [filterType, filterProject, filterUser].filter((f) => f !== "all").length;

    return (
        <div className="space-y-4 animate-fade-in" data-testid="calendar-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">Calendrier</h1>
                    <p className="text-muted-foreground mt-1">
                        Pilotez vos opérations depuis un calendrier central
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={handleExportIcal}
                        disabled={exporting}
                        data-testid="export-ical-btn"
                    >
                        {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                        Exporter (iCal)
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => setShowFilters(!showFilters)}
                        data-testid="toggle-filters-btn"
                    >
                        <Filter className="w-4 h-4 mr-2" />
                        Filtres
                        {activeFilterCount > 0 && (
                            <Badge variant="secondary" className="ml-2 px-1.5 py-0 text-xs">
                                {activeFilterCount}
                            </Badge>
                        )}
                    </Button>
                    <Button
                        onClick={() => {
                            setIsEditing(false);
                            const now = new Date();
                            setFormData({
                                ...defaultForm,
                                start: now.toISOString().slice(0, 16),
                                end: new Date(now.getTime() + 3600000).toISOString().slice(0, 16),
                            });
                            setDialogOpen(true);
                        }}
                        data-testid="add-event-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvel événement
                    </Button>
                </div>
            </div>

            {/* Filters */}
            {showFilters && (
                <Card className="animate-fade-in">
                    <CardContent className="p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div>
                                <Label className="text-xs text-muted-foreground mb-1 block">Type</Label>
                                <Select value={filterType} onValueChange={setFilterType}>
                                    <SelectTrigger data-testid="filter-type">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous les types</SelectItem>
                                        <SelectItem value="meeting">Réunions</SelectItem>
                                        <SelectItem value="task_deadline">Tâches</SelectItem>
                                        <SelectItem value="project_deadline">Projets</SelectItem>
                                        <SelectItem value="reminder">Rappels</SelectItem>
                                        <SelectItem value="custom">Événements</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground mb-1 block">Projet</Label>
                                <Select value={filterProject} onValueChange={setFilterProject}>
                                    <SelectTrigger data-testid="filter-project">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous les projets</SelectItem>
                                        {projects.map((p) => (
                                            <SelectItem key={p.project_id} value={p.project_id}>
                                                {p.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground mb-1 block">Utilisateur</Label>
                                <Select value={filterUser} onValueChange={setFilterUser}>
                                    <SelectTrigger data-testid="filter-user">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous les utilisateurs</SelectItem>
                                        {employees.map((e) => (
                                            <SelectItem key={e.employee_id} value={e.employee_id}>
                                                {e.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        {activeFilterCount > 0 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="mt-3"
                                onClick={() => {
                                    setFilterType("all");
                                    setFilterProject("all");
                                    setFilterUser("all");
                                }}
                            >
                                Réinitialiser les filtres
                            </Button>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Legend */}
            <div className="flex flex-wrap gap-3">
                {Object.entries(EVENT_TYPE_LABELS).map(([key, label]) => (
                    <div key={key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: EVENT_TYPE_COLORS[key] }}
                        />
                        {label}
                    </div>
                ))}
            </div>

            {/* Calendar */}
            <Card data-testid="calendar-card">
                <CardContent className="p-2 sm:p-4 calendar-wrapper">
                    <FullCalendar
                        ref={calendarRef}
                        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                        initialView="dayGridMonth"
                        locale={frLocale}
                        headerToolbar={{
                            left: "prev,next today",
                            center: "title",
                            right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
                        }}
                        buttonText={{
                            today: "Aujourd'hui",
                            month: "Mois",
                            week: "Semaine",
                            day: "Jour",
                            list: "Liste",
                        }}
                        events={calendarEvents}
                        dateClick={handleDateClick}
                        eventClick={handleEventClick}
                        editable={false}
                        selectable={true}
                        dayMaxEvents={3}
                        height="auto"
                        nowIndicator={true}
                        eventDisplay="block"
                        eventTimeFormat={{
                            hour: "2-digit",
                            minute: "2-digit",
                            meridiem: false,
                            hour12: false,
                        }}
                    />
                </CardContent>
            </Card>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading">
                            {isEditing ? "Modifier l'événement" : "Nouvel événement"}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <Label>Titre *</Label>
                            <Input
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="Nom de l'événement"
                                data-testid="event-title-input"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Type</Label>
                                <Select
                                    value={formData.event_type}
                                    onValueChange={(v) =>
                                        setFormData({
                                            ...formData,
                                            event_type: v,
                                            color: EVENT_TYPE_COLORS[v] || EVENT_TYPE_COLORS.custom,
                                        })
                                    }
                                >
                                    <SelectTrigger data-testid="event-type-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="meeting">Réunion</SelectItem>
                                        <SelectItem value="reminder">Rappel</SelectItem>
                                        <SelectItem value="custom">Événement libre</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-end gap-2 pb-1">
                                <Switch
                                    checked={formData.all_day}
                                    onCheckedChange={(c) => setFormData({ ...formData, all_day: c })}
                                />
                                <Label className="text-sm">Toute la journée</Label>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Début</Label>
                                <Input
                                    type={formData.all_day ? "date" : "datetime-local"}
                                    value={formData.all_day ? formData.start.slice(0, 10) : formData.start}
                                    onChange={(e) => setFormData({ ...formData, start: e.target.value })}
                                    data-testid="event-start-input"
                                />
                            </div>
                            <div>
                                <Label>Fin</Label>
                                <Input
                                    type={formData.all_day ? "date" : "datetime-local"}
                                    value={formData.all_day ? (formData.end || "").slice(0, 10) : formData.end}
                                    onChange={(e) => setFormData({ ...formData, end: e.target.value })}
                                    data-testid="event-end-input"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Projet</Label>
                                <Select
                                    value={formData.project_id || "none"}
                                    onValueChange={(v) => setFormData({ ...formData, project_id: v === "none" ? "" : v })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Aucun" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Aucun</SelectItem>
                                        {projects.map((p) => (
                                            <SelectItem key={p.project_id} value={p.project_id}>
                                                {p.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Assigné à</Label>
                                <Select
                                    value={formData.assigned_to || "none"}
                                    onValueChange={(v) => setFormData({ ...formData, assigned_to: v === "none" ? "" : v })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Personne" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Personne</SelectItem>
                                        {employees.map((e) => (
                                            <SelectItem key={e.employee_id} value={e.employee_id}>
                                                {e.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div>
                            <Label>Lieu</Label>
                            <Input
                                value={formData.location}
                                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                placeholder="Salle de réunion, visio..."
                            />
                        </div>

                        <div>
                            <Label>Description</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                rows={2}
                                placeholder="Détails de l'événement..."
                            />
                        </div>

                        <div>
                            <Label>Couleur</Label>
                            <div className="flex gap-2 mt-1">
                                {Object.entries(EVENT_TYPE_COLORS).map(([key, color]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        className={`w-7 h-7 rounded-full transition-all ${
                                            formData.color === color ? "ring-2 ring-offset-2 ring-primary scale-110" : "hover:scale-105"
                                        }`}
                                        style={{ backgroundColor: color }}
                                        onClick={() => setFormData({ ...formData, color })}
                                        title={EVENT_TYPE_LABELS[key]}
                                    />
                                ))}
                            </div>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button type="submit" disabled={submitting} data-testid="submit-event-btn">
                                {submitting ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : null}
                                {isEditing ? "Mettre à jour" : "Créer"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Event Detail Dialog */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="max-w-md">
                    {selectedEvent && (
                        <>
                            <DialogHeader>
                                <div className="flex items-center gap-3">
                                    <div
                                        className="w-4 h-4 rounded-full flex-shrink-0"
                                        style={{
                                            backgroundColor:
                                                selectedEvent.color ||
                                                EVENT_TYPE_COLORS[selectedEvent.event_type] ||
                                                EVENT_TYPE_COLORS.custom,
                                        }}
                                    />
                                    <DialogTitle className="font-heading text-lg">{selectedEvent.title}</DialogTitle>
                                </div>
                            </DialogHeader>
                            <div className="space-y-3">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Badge variant="outline" className="text-xs">
                                        {EVENT_TYPE_LABELS[selectedEvent.event_type] || selectedEvent.event_type}
                                    </Badge>
                                    {selectedEvent.source !== "manual" && (
                                        <Badge variant="secondary" className="text-xs">
                                            Auto
                                        </Badge>
                                    )}
                                </div>

                                <div className="flex items-start gap-2 text-sm">
                                    <Clock className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                                    <div>
                                        <p>
                                            {selectedEvent.all_day
                                                ? new Date(selectedEvent.start).toLocaleDateString("fr-FR", {
                                                      weekday: "long",
                                                      day: "numeric",
                                                      month: "long",
                                                      year: "numeric",
                                                  })
                                                : new Date(selectedEvent.start).toLocaleString("fr-FR", {
                                                      weekday: "long",
                                                      day: "numeric",
                                                      month: "long",
                                                      hour: "2-digit",
                                                      minute: "2-digit",
                                                  })}
                                        </p>
                                        {selectedEvent.end && selectedEvent.end !== selectedEvent.start && (
                                            <p className="text-muted-foreground">
                                                → {selectedEvent.all_day
                                                    ? new Date(selectedEvent.end).toLocaleDateString("fr-FR")
                                                    : new Date(selectedEvent.end).toLocaleString("fr-FR", {
                                                          hour: "2-digit",
                                                          minute: "2-digit",
                                                      })}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {selectedEvent.location && (
                                    <div className="flex items-center gap-2 text-sm">
                                        <MapPin className="w-4 h-4 text-muted-foreground" />
                                        {selectedEvent.location}
                                    </div>
                                )}

                                {selectedEvent.project_name && (
                                    <div className="flex items-center gap-2 text-sm">
                                        <FolderKanban className="w-4 h-4 text-muted-foreground" />
                                        {selectedEvent.project_name}
                                    </div>
                                )}

                                {selectedEvent.assigned_to_name && (
                                    <div className="flex items-center gap-2 text-sm">
                                        <User className="w-4 h-4 text-muted-foreground" />
                                        {selectedEvent.assigned_to_name}
                                    </div>
                                )}

                                {selectedEvent.description && (
                                    <div className="text-sm text-muted-foreground pt-2 border-t">
                                        {selectedEvent.description}
                                    </div>
                                )}
                            </div>

                            {selectedEvent.source === "manual" && (
                                <DialogFooter className="gap-2">
                                    <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={handleDelete}
                                        data-testid="delete-event-btn"
                                    >
                                        <Trash2 className="w-4 h-4 mr-1" />
                                        Supprimer
                                    </Button>
                                    <Button size="sm" onClick={openEditDialog} data-testid="edit-event-btn">
                                        Modifier
                                    </Button>
                                </DialogFooter>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};
